/**
 * PII Validator Utility
 *
 * Provides client-side validation to detect Personally Identifiable Information (PII)
 * in text inputs. This helps prevent users from accidentally entering sensitive data
 * in student-facing notes.
 *
 * @module piiValidator
 */

/**
 * Regular expression patterns for detecting different types of PII
 * Note: Functions return new RegExp instances to avoid state issues with global flag
 */
const PII_PATTERNS = {
    /** Matches email addresses in format: username@domain.tld */
    email: () => /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}/g,

    /** Matches US phone numbers with various formats: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXX.XXX.XXXX, XXXXXXXXXX */
    phone: () => /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,

    /** Matches Social Security Numbers: XXX-XX-XXXX or XXXXXXXXX */
    ssn: () => /\d{3}-?\d{2}-?\d{4}/g,

    /** Matches credit card numbers: XXXX XXXX XXXX XXXX, XXXX-XXXX-XXXX-XXXX, or XXXXXXXXXXXXXXXX */
    creditCard: () => /\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}/g,
};

/** Matches URLs to strip them before PII detection (avoids false positives from UUIDs/params in URLs) */
const URL_PATTERN = /https?:\/\/[^\s]+/g;

/**
 * Removes URLs from text to prevent false positive PII matches
 * against UUIDs, query parameters, and other URL components.
 */
const stripUrls = (text: string): string => text.replace(URL_PATTERN, '');

/**
 * Checks if the provided text contains any PII patterns
 *
 * @param text - The text to validate for PII
 * @returns true if PII is detected, false otherwise
 *
 * @example
 * ```typescript
 * containsPII('Contact me at test@example.com'); // returns true
 * containsPII('This is clean instructional text'); // returns false
 * containsPII(''); // returns false
 * ```
 */
export const containsPII = (text: string): boolean => {
    if (!text) return false;
    const textWithoutUrls = stripUrls(text);
    return Object.values(PII_PATTERNS).some((patternFn) =>
        patternFn().test(textWithoutUrls),
    );
};

/**
 * Identifies which types of PII are present in the text
 *
 * @param text - The text to analyze for PII types
 * @returns Array of PII type names found in the text
 *
 * @example
 * ```typescript
 * getPIITypes('Email: test@example.com Phone: 555-1234567');
 * // returns ['email', 'phone']
 *
 * getPIITypes('Clean text');
 * // returns []
 * ```
 */
export const getPIITypes = (text: string): string[] => {
    if (!text) return [];
    const textWithoutUrls = stripUrls(text);
    return Object.entries(PII_PATTERNS)
        .filter(([_, patternFn]) => patternFn().test(textWithoutUrls))
        .map(([type]) => type);
};
